/**
 * productCodes — código interno (SKU) do prestador e o gerador aleatório.
 *
 * DUAS COISAS DIFERENTES, de propósito:
 *  - `barcode` é o **GTIN do fabricante** (EAN-13 impresso na embalagem). Ele
 *    identifica o produto no mundo inteiro e NÃO se inventa: um código gerado
 *    por nós poderia coincidir com o GTIN real de outro produto e, além de
 *    errado numa etiqueta, faria o scanner do próprio app achar o item errado.
 *  - `internal_code` é a **referência interna** ("CAM-P-AZ", "A102"). É dele
 *    que o gerador `↻` cuida — é exatamente a resposta para quem não tem
 *    etiqueta de fábrica (artesanato, serviço embalado, produção própria).
 *
 * Os dois coexistem no mesmo produto e na mesma variação.
 */

/** Mesmo limite que o banco impõe (products_internal_code_len_chk). */
export const INTERNAL_CODE_MAX_LENGTH = 64;

/**
 * Alfabeto do gerador: maiúsculas e dígitos SEM os pares que se confundem à
 * mão e no papel — `O`/`0`, `I`/`1`, `S`/`5`. O código é feito para ser
 * escrito numa etiqueta e digitado de volta na busca.
 */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRTUVWXYZ2346789';

/** Tamanho da parte aleatória. 6 chars ≈ 729 milhões de combinações. */
const RANDOM_LENGTH = 6;

/** Quantas vezes tentar antes de desistir (a colisão é astronomicamente rara). */
const MAX_ATTEMPTS = 25;

/**
 * Normaliza um código digitado: sem espaços nas pontas, maiúsculas, e vazio
 * vira `null` ("não informado"). Não força formato — o prestador que já usa
 * "cam-p-az" pode continuar usando.
 */
export function normalizeInternalCode(raw: string | null | undefined): string | null {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  return text.toUpperCase().slice(0, INTERNAL_CODE_MAX_LENGTH);
}

/** O código cabe no limite do banco? (a UI barra antes de tentar salvar) */
export function isValidInternalCode(raw: string | null | undefined): boolean {
  const text = String(raw ?? '').trim();
  return text.length <= INTERNAL_CODE_MAX_LENGTH;
}

/**
 * Conjunto de códigos já usados na empresa, normalizado para comparação.
 * Aceita a lista plana de `products` (pais e variações misturados, como o banco
 * devolve) e também os rascunhos de variação ainda não salvos.
 */
export function usedInternalCodes(
  products: Array<{ internal_code?: string | null } | null | undefined>,
  extra: Array<string | null | undefined> = [],
): Set<string> {
  const used = new Set<string>();
  for (const p of products ?? []) {
    const code = normalizeInternalCode(p?.internal_code);
    if (code) used.add(code);
  }
  for (const raw of extra) {
    const code = normalizeInternalCode(raw);
    if (code) used.add(code);
  }
  return used;
}

/**
 * ─── O CÓDIGO SUGERIDO SAI DO NOME, e o motivo é o uso dele ─────────────────
 *
 * O gerador antigo devolvia 6 caracteres aleatórios (`K7P2R9`). Ele garantia
 * unicidade e não servia para nada mais: o código interno existe para o
 * prestador escrever numa etiqueta, ler numa prateleira e digitar de volta na
 * busca — e para isso ele precisa DIZER de que produto se trata. Com códigos
 * aleatórios, olhar a etiqueta não responde a pergunta que ela existe para
 * responder, e a lista do catálogo vira uma coluna de ruído.
 *
 * A forma: até três pedaços do nome, separados por hífen —
 *
 *     "Camiseta Polo Azul"        → CAM-POL-AZU
 *     "Instalação"                → INST
 *     "Ar-condicionado 12000 BTU" → AR-CON-120
 *     "Cabo flexível 2,5mm"       → CAB-FLE-2   (a vírgula separa)
 *
 * Palavra ÚNICA vira um pedaço de 4 (`INST` lê melhor que `INS`); com duas ou
 * mais, cada pedaço tem 3 — é o que mantém o código curto o bastante para caber
 * numa etiqueta e ainda distinguir "Cabo flexível" de "Cabo coaxial".
 *
 * Acento é dobrado (`Instalação` → `INSTALACAO`) e tudo que não é letra ou
 * dígito vira separador, porque o código é digitado num teclado de celular e no
 * campo de busca: um `ç` ou um `²` ali é um caractere que ninguém acerta.
 *
 * ⚠️ NÃO é identificador: colisão é resolvida com sufixo numérico, não com
 * recusa. Dois produtos "Cabo flexível 2,5mm" e "Cabo flexível 4mm" produzem a
 * mesma base (`CAB-FLE-2` e `CAB-FLE-4`... e "Cabo flexível 2mm" bate com o
 * primeiro), e o segundo da série vira `CAB-FLE-2-2`.
 */

/** Palavras que não distinguem nada e só gastam caracteres do código. */
const STOP_WORDS = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'E', 'COM', 'PARA', 'EM', 'NO', 'NA', 'A', 'O']);

/** Quantos pedaços do nome entram no código. Além disso ele deixa de caber na etiqueta. */
const NAME_CHUNKS = 3;

/**
 * O nome → a BASE do código (sem sufixo de colisão), ou `null` quando o nome não
 * rende nada aproveitável (vazio, ou só símbolos).
 */
export function internalCodeBaseFromName(name: string | null | undefined): string | null {
  const words = String(name ?? '')
    .normalize('NFD')
    // Diacríticos por ESCAPE, e não pelos caracteres combinantes literais: escritos
    // à mão eles são invisíveis no fonte e ninguém consegue revisar (#190d).
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);

  // As stop-words só saem se sobrar alguma coisa: um produto chamado "Kit de"
  // ficaria sem código nenhum se elas fossem removidas incondicionalmente.
  const significant = words.filter((w) => !STOP_WORDS.has(w));
  const chosen = (significant.length > 0 ? significant : words).slice(0, NAME_CHUNKS);
  if (chosen.length === 0) return null;

  const size = chosen.length === 1 ? 4 : 3;
  return chosen.map((word) => word.slice(0, size)).join('-').slice(0, INTERNAL_CODE_MAX_LENGTH);
}

/**
 * O código sugerido para um produto, a partir do NOME dele.
 *
 * Cai no gerador aleatório quando o nome ainda não foi digitado (é o caso real:
 * o usuário pode clicar em `↻` antes de nomear o item) — um código é sempre
 * melhor do que um campo vazio, e ele se torna legível assim que o usuário
 * gerar de novo com o nome preenchido.
 *
 * O sufixo de colisão começa em `-2` porque o primeiro da série é o código sem
 * sufixo: `CAB-FLE`, `CAB-FLE-2`, `CAB-FLE-3`.
 */
export function suggestInternalCode(
  name: string | null | undefined,
  used: Set<string> | Iterable<string> = new Set<string>(),
  random: () => number = Math.random,
): string | null {
  const taken = new Set<string>();
  for (const code of used instanceof Set ? used : new Set(used)) {
    const n = normalizeInternalCode(code);
    if (n) taken.add(n);
  }

  const base = internalCodeBaseFromName(name);
  if (!base) return generateInternalCode(taken, random);

  if (!taken.has(base)) return base;
  for (let suffix = 2; suffix <= MAX_ATTEMPTS; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
  // Nome muito repetido (25 produtos com o mesmo começo). Melhor um código
  // aleatório do que nenhum — e ele continua editável na tela.
  return generateInternalCode(taken, random);
}

function randomChunk(random: () => number): string {
  let out = '';
  for (let i = 0; i < RANDOM_LENGTH; i += 1) {
    out += CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)];
  }
  return out;
}

/**
 * Gera um código interno que ainda não existe em `used`.
 *
 * `random` é injetável só para o teste poder forçar uma COLISÃO e provar que a
 * função não devolve um código repetido — sem isso o guard de unicidade seria
 * um teste de sorte (a chance real de colidir é ~1 em 700 milhões).
 *
 * Devolve `null` quando não conseguiu um código livre em `MAX_ATTEMPTS`
 * tentativas: melhor a UI dizer "tente de novo" do que gravar um duplicado.
 */
export function generateInternalCode(
  used: Set<string> | Iterable<string> = new Set<string>(),
  random: () => number = Math.random,
): string | null {
  const taken = used instanceof Set ? used : new Set(used);
  const normalizedTaken = new Set<string>();
  for (const code of taken) {
    const n = normalizeInternalCode(code);
    if (n) normalizedTaken.add(n);
  }

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const candidate = randomChunk(random);
    if (!normalizedTaken.has(candidate)) return candidate;
  }
  return null;
}

/**
 * O termo de busca casa com o código interno deste produto?
 *
 * Compara normalizado dos dois lados para que procurar "cam-p" ache "CAM-P-AZ"
 * mesmo o usuário digitando em minúsculas.
 */
export function matchesInternalCode(product: { internal_code?: string | null }, term: string): boolean {
  const code = normalizeInternalCode(product?.internal_code);
  const needle = normalizeInternalCode(term);
  if (!code || !needle) return false;
  return code.includes(needle);
}
